import {
  ApolloClient,
  ApolloLink,
  type FieldMergeFunction,
  InMemoryCache,
} from "@apollo/client/core";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { setContext } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs";
import { store } from "../store";
import { logout } from "../store/slices/authSlice";
import { storage } from "./storage";

const GRAPHQL_ENDPOINT = import.meta.env.VITE_API_URL || "http://localhost:4002/graphql";

if (!import.meta.env.VITE_API_URL) {
  console.warn("⚠️ VITE_API_URL not set, using default: http://localhost:4002/graphql");
}

const uploadLink = new UploadHttpLink({
  uri: GRAPHQL_ENDPOINT,
  credentials: "include", // Send/receive CloudFront signed cookies across the web/api subdomains
  headers: {
    // Required for Apollo Server 5 CSRF protection with multipart requests
    "apollo-require-preflight": "true",
  },
});

const authLink = setContext((_, { headers }) => {
  const token = store.getState().auth.accessToken;
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const errorLink = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    for (const err of error.errors) {
      if (
        err.extensions?.code === "UNAUTHENTICATED" ||
        err.message.includes("Invalid or expired token")
      ) {
        storage.clearTokens();
        store.dispatch(logout());
        window.location.href = "/login";
      }
    }
  } else {
    console.error("Network error:", error);
  }
});

// Deduplicating merge function for cursor-based pagination
type Edge = { node: { __typename?: string; [key: string]: unknown }; cursor: string };
type Connection = { edges: Edge[]; pageInfo: { hasNextPage: boolean; endCursor: string | null } };

const mergeDeduplicated: FieldMergeFunction<Connection> = (existing, incoming, { readField }) => {
  if (!existing) return incoming;

  const mergedEdges = [...existing.edges];
  const existingIdMap = new Map<string, number>();

  mergedEdges.forEach((edge, index) => {
    // biome-ignore lint/suspicious/noExplicitAny: Apollo readField requires flexible type
    const id = readField<string>("id", edge.node as any);
    if (id) {
      existingIdMap.set(id, index);
    }
  });

  for (const edge of incoming.edges) {
    // biome-ignore lint/suspicious/noExplicitAny: Apollo readField requires flexible type
    const id = readField<string>("id", edge.node as any);
    if (id && existingIdMap.has(id)) {
      const index = existingIdMap.get(id);
      if (index !== undefined) {
        mergedEdges[index] = edge;
      }
    } else if (id) {
      mergedEdges.push(edge);
    }
  }

  return {
    ...incoming,
    edges: mergedEdges,
  };
};

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, uploadLink]),
  cache: new InMemoryCache({
    // Tell Apollo which concrete types implement the Media interface
    // This is critical for proper cache normalization with interfaces/unions
    possibleTypes: {
      Media: ["Image", "Video", "Audio", "File"],
    },
    typePolicies: {
      Query: {
        fields: {
          media: {
            keyArgs: ["folderId", "type"],
            merge: mergeDeduplicated, // Use smart merge to prevent duplicates
          },
          // TODO: pagination for folders query
          // folders: {
          //   keyArgs: ["parentId", "type"],
          //   merge: mergeDeduplicated,
          // },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
    mutate: {
      awaitRefetchQueries: true,
    },
  },
});
